import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker, Polyline } from '@react-google-maps/api';

const LIBRARIES = ['places', 'geometry'];

const containerStyle = {
  width: '100%',
  height: '350px',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
};

/** Light, low-clutter map — matches carpool “modern” purple UI */
const HERO_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#eef2f7' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'poi', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
];

const containerStyleHero = {
  width: '100%',
  height: '100%',
  minHeight: '200px',
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none',
};

const RouteMap = ({ origin, destination, waypoints = [], variant = 'default' }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [fallbackMode, setFallbackMode] = useState(false);
  const directionsServiceRef = useRef(null);

  useEffect(() => {
    if (!isLoaded || !window.google) return;

    if (!origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
      return;
    }

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }

    const mappedWaypoints = waypoints
      .filter(w => w.lat && w.lng)
      .map(w => ({
        location: new window.google.maps.LatLng(w.lat, w.lng),
        stopover: true
      }));

    directionsServiceRef.current.route(
      {
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: new window.google.maps.LatLng(destination.lat, destination.lng),
        waypoints: mappedWaypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
          setErrorMsg('');
          setFallbackMode(false);
        } else {
          console.warn('Directions request failed:', status);
          setFallbackMode(true);
          if (status === 'REQUEST_DENIED') {
            setErrorMsg('Инфо: Directions API не е активен. Показване на директна връзка.');
          } else {
            setErrorMsg('Инфо: Не може да се изчисли пътя. (' + status + ')');
          }
        }
      }
    );
  }, [isLoaded, origin, destination, waypoints]);

  const isHero = variant === 'hero';
  const mapBox = isHero ? containerStyleHero : containerStyle;
  const routeStroke = isHero ? '#7c3aed' : '#4f46e5';
  const fallbackStroke = isHero ? '#7c3aed' : '#ef4444';

  const placeholderBase = {
    ...mapBox,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: isHero ? '16px' : '12px',
    boxSizing: 'border-box',
  };

  if (!apiKey) {
    return (
      <div
        className={isHero ? 'route-map-hero route-map-hero--placeholder' : 'map-placeholder error'}
        style={{
          ...placeholderBase,
          background: isHero ? 'linear-gradient(160deg, #e2e8f0 0%, #cbd5e1 100%)' : '#fee2e2',
          color: isHero ? '#475569' : '#dc2626',
          fontSize: isHero ? '14px' : undefined,
        }}
      >
        <span>{isHero ? 'Добавете REACT_APP_GOOGLE_MAPS_API_KEY за интерактивна карта.' : '⚠️ Google Maps API key is missing.'}</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className={isHero ? 'route-map-hero route-map-hero--placeholder' : 'map-placeholder error'}
        style={{
          ...placeholderBase,
          background: isHero ? '#fef2f2' : '#fee2e2',
          color: isHero ? '#b91c1c' : '#dc2626',
          fontSize: '13px',
        }}
      >
        <span>{loadError.message}</span>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={isHero ? 'route-map-hero route-map-hero--placeholder' : 'map-placeholder'}
        style={{
          ...placeholderBase,
          background: isHero ? 'linear-gradient(160deg, #f1f5f9 0%, #e2e8f0 100%)' : '#f8fafc',
          color: '#64748b',
        }}
      >
        <span>Зареждане на картата...</span>
      </div>
    );
  }

  if (!origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
    return (
      <div
        className={isHero ? 'route-map-hero route-map-hero--placeholder' : 'map-placeholder'}
        style={{
          ...placeholderBase,
          background: isHero ? 'linear-gradient(160deg, #f8fafc 0%, #e2e8f0 100%)' : '#f8fafc',
          color: '#64748b',
          fontSize: isHero ? '14px' : undefined,
        }}
      >
        <span>Липсват валидни координати за маршрута.</span>
      </div>
    );
  }

  // Calculate generic center for fallback
  const fallbackCenter = {
    lat: (Number(origin.lat) + Number(destination.lat)) / 2,
    lng: (Number(origin.lng) + Number(destination.lng)) / 2
  };

  const mapOptions = isHero
    ? {
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        styles: HERO_MAP_STYLES,
      }
    : {
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      };

  return (
    <div
      className={isHero ? 'route-map-container route-map-container--hero' : 'route-map-container'}
      style={{ width: '100%', height: isHero ? '100%' : undefined, minHeight: isHero ? '100%' : undefined, position: 'relative' }}
    >
      {errorMsg && (
        <div
          className={isHero ? 'route-map-hero-banner' : undefined}
          style={
            isHero
              ? {
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  right: 10,
                  zIndex: 2,
                  color: '#9a3412',
                  fontSize: '12px',
                  textAlign: 'center',
                  background: 'rgba(255, 247, 237, 0.95)',
                  padding: '8px 10px',
                  borderRadius: '10px',
                  border: '1px solid #ffedd5',
                  backdropFilter: 'blur(8px)',
                }
              : {
                  marginBottom: '8px',
                  color: '#ea580c',
                  fontSize: '13px',
                  textAlign: 'center',
                  background: '#fff7ed',
                  padding: '6px',
                  borderRadius: '6px',
                  border: '1px solid #ffedd5',
                }
          }
        >
          {errorMsg}
        </div>
      )}
      <GoogleMap
        mapContainerStyle={mapBox}
        center={fallbackMode ? fallbackCenter : origin}
        zoom={fallbackMode ? 10 : 12}
        options={mapOptions}
      >
        {directionsResponse && (
          <DirectionsRenderer
            directions={directionsResponse}
            options={{
              polylineOptions: {
                strokeColor: routeStroke,
                strokeWeight: 5,
                strokeOpacity: 0.92,
              },
            }}
          />
        )}

        {fallbackMode && (
          <>
            <Marker position={origin} label="А" />
            <Marker position={destination} label="Б" />

            {waypoints
              .filter(w => w.lat && w.lng)
              .map((wp, i) => (
                <Marker
                  key={i}
                  position={{ lat: Number(wp.lat), lng: Number(wp.lng) }}
                  icon="http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                />
              ))}

            <Polyline
              path={[
                origin,
                ...waypoints.filter(w => w.lat && w.lng).map(w => ({ lat: Number(w.lat), lng: Number(w.lng) })),
                destination,
              ]}
              options={{
                strokeColor: fallbackStroke,
                strokeOpacity: 0.88,
                strokeWeight: isHero ? 5 : 4,
                geodesic: true,
              }}
            />
          </>
        )}
      </GoogleMap>
    </div>
  );
};

export default React.memo(RouteMap);
